import { useEffect, useState, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { STORE } from "@/constants/testIds";
import ProductCard from "@/components/ProductCard";
import Marquee from "@/components/Marquee";
import FeaturedRail from "@/components/FeaturedRail";
import { ArrowRight, Search, X } from "lucide-react";

const CATS = [
  { key: "all", label: "All Drops" },
  { key: "ebook", label: "E-Books" },
  { key: "music", label: "Music" },
  { key: "podcast", label: "Podcasts" },
  { key: "image", label: "Images" },
  { key: "video", label: "Video" },
  { key: "bundle", label: "Bundles" },
];

const SORTS = [
  { key: "new", label: "Newest" },
  { key: "trending", label: "Trending" },
  { key: "discover", label: "Discover" },
];

function debounce(fn, ms) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

export default function Marketplace() {
  const [sp, setSp] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [topTags, setTopTags] = useState([]);
  const [cat, setCat] = useState(sp.get("category") || "all");
  const [sort, setSort] = useState(sp.get("sort") || "new");
  const [q, setQ] = useState(sp.get("q") || "");
  const [tags, setTags] = useState(sp.get("tags") || "");
  const [loading, setLoading] = useState(true);

  // Load top tags once
  useEffect(() => {
    api.get("/products/tags", { params: { limit: 20 } })
      .then(({ data }) => setTopTags(data))
      .catch(() => setTopTags([]));
  }, []);

  // Fetch products when filters change
  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const params = { sort };
    if (cat !== "all") params.category = cat;
    if (q.trim()) params.q = q.trim();
    if (tags.trim()) params.tags = tags.trim();
    api.get("/products", { params })
      .then(({ data }) => { if (!cancel) setProducts(data); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [cat, sort, q, tags]);

  // Sync URL for shareable links
  useEffect(() => {
    const next = new URLSearchParams();
    if (cat !== "all") next.set("category", cat);
    if (sort !== "new") next.set("sort", sort);
    if (q.trim()) next.set("q", q.trim());
    if (tags.trim()) next.set("tags", tags.trim());
    setSp(next, { replace: true });
  }, [cat, sort, q, tags, setSp]);

  const activeTags = useMemo(
    () => tags.split(",").map((t) => t.trim().toLowerCase()).filter(Boolean),
    [tags]
  );
  const toggleTag = (t) => {
    const has = activeTags.includes(t);
    const next = has ? activeTags.filter((x) => x !== t) : [...activeTags, t];
    setTags(next.join(","));
  };
  const clearFilters = () => { setQ(""); setTags(""); setCat("all"); };
  const anyFilterActive = q || tags || cat !== "all";

  return (
    <div>
      <section data-testid={STORE.hero} className="relative border-b border-white/10">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 lg:py-24 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#CCFF00] mb-6">
              // A shared store for limited edition digital work
            </div>
            <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-8xl tracking-tighter leading-[0.95]">
              Rare digital<br />
              goods.{" "}
              <span className="text-[#FF3366]">Numbered.</span><br />
              <span className="text-white/50">Signed by scarcity.</span>
            </h1>
            <p className="mt-8 text-white/70 max-w-xl leading-relaxed">
              Publish PDFs, tracks, podcasts, images, video and asset-pack bundles as limited drops. Set the edition size,
              set the price, disappear when the last copy sells.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                to="/studio"
                className="inline-flex items-center gap-2 bg-[#FF3366] text-white font-mono uppercase tracking-[0.2em] text-xs px-5 py-3 hover:bg-[#FF3366]/90 transition-transform hover:scale-[1.02]"
              >
                Open Studio <ArrowRight className="w-3.5 h-3.5" />
              </Link>
              <a
                href="#drops"
                className="inline-flex items-center gap-2 border border-white/20 hover:border-white/40 font-mono uppercase tracking-[0.2em] text-xs px-5 py-3"
              >
                Browse Drops
              </a>
            </div>
          </div>
          <div className="lg:col-span-4 relative">
            <div className="relative border border-white/10 aspect-[3/4] overflow-hidden">
              <img
                src="https://images.pexels.com/photos/13156182/pexels-photo-13156182.jpeg"
                className="w-full h-full object-cover"
                alt="hero"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#CCFF00]">Editorial Pick</div>
                <div className="font-display font-black text-2xl mt-1">Curated by creators.</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      <FeaturedRail />

      <section id="drops" className="mx-auto max-w-[1400px] px-6 lg:px-10 py-14">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-6">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/50">// The Shared Store</div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight mt-2">Active drops</h2>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
              <input
                data-testid="store-search-input"
                type="search"
                value={q}
                onChange={debounceOnChange(setQ)}
                defaultValue={q}
                placeholder="Search drops…"
                className="brutal-input pl-9 pr-3 py-2 text-sm w-56"
              />
            </div>
            <div className="flex gap-1 border border-white/15 p-0.5" data-testid="store-sort">
              {SORTS.map((s) => (
                <button
                  key={s.key}
                  data-testid={`store-sort-${s.key}`}
                  onClick={() => setSort(s.key)}
                  className={`font-mono text-[11px] uppercase tracking-[0.2em] px-3 py-1.5 ${sort === s.key ? "bg-white text-black" : "text-white/70 hover:text-white"}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {CATS.map((c) => (
            <button
              key={c.key}
              data-testid={STORE.categoryFilter(c.key)}
              onClick={() => setCat(c.key)}
              className={`font-mono text-[11px] uppercase tracking-[0.2em] px-3 py-2 border ${cat === c.key ? "bg-white text-black border-white" : "border-white/20 text-white/70 hover:border-white/50"}`}
            >
              {c.label}
            </button>
          ))}
        </div>

        {topTags.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="store-tags">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">// Tags</span>
            {topTags.map((t) => {
              const active = activeTags.includes(t.tag);
              return (
                <button
                  key={t.tag}
                  data-testid={`store-tag-${t.tag}`}
                  onClick={() => toggleTag(t.tag)}
                  className={`font-mono text-[10px] uppercase tracking-[0.2em] border px-2 py-1 ${active ? "border-[#CCFF00] bg-[#CCFF00]/10 text-[#CCFF00]" : "border-white/15 text-white/60 hover:border-white/40"}`}
                >
                  #{t.tag} <span className="opacity-50">·{t.count}</span>
                </button>
              );
            })}
          </div>
        )}

        {anyFilterActive && (
          <div className="mb-4">
            <button
              data-testid="store-clear-filters"
              onClick={clearFilters}
              className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60 hover:text-white"
            >
              <X className="w-3 h-3" /> Clear filters
            </button>
          </div>
        )}

        {loading ? (
          <div className="font-mono text-xs text-white/50">Loading drops…</div>
        ) : products.length === 0 ? (
          <div
            data-testid={STORE.emptyState}
            className="border border-dashed border-white/15 py-24 text-center"
          >
            <div className="font-mono text-[11px] uppercase tracking-[0.3em] text-white/40">// EMPTY</div>
            <p className="mt-4 text-white/70">
              {anyFilterActive ? "No drops match your filters." : "No drops here yet. Be the first to publish one."}
            </p>
            {anyFilterActive ? (
              <button onClick={clearFilters} className="inline-block mt-6 border border-white/20 font-mono text-xs uppercase tracking-[0.2em] px-4 py-2">
                Clear filters →
              </button>
            ) : (
              <Link to="/studio" className="inline-block mt-6 bg-[#CCFF00] text-black font-mono text-xs uppercase tracking-[0.2em] px-4 py-2 hover:opacity-90">
                Create the first drop →
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {products.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}
      </section>
    </div>
  );
}

// Debounce user input to avoid firing on every keystroke
const debouncedSetters = new WeakMap();
function debounceOnChange(setter) {
  if (!debouncedSetters.has(setter)) {
    debouncedSetters.set(setter, debounce(setter, 250));
  }
  const deb = debouncedSetters.get(setter);
  return (e) => deb(e.target.value);
}
